import { useMutation, UseMutationResult, useQueryClient } from 'react-query';
import {
  QueryId,
  TOTAL_PRICE_DEPENDENT_QUERIES,
} from '@/integrations/content/utils';
import getCulture from '@/libraries/getters/get-culture';
import getLocale from '@/libraries/getters/get-locale';

const MY_TRIP_PRODUCT_INVALIDATION_KEYS = [
  QueryId.MY_TRIP_BOOKING_OVERVIEW,
  QueryId.MY_TRIP_PASSENGER_SUMMARY,
  QueryId.MY_TRIP_PRODUCT_CAROUSEL,
];

type UseMutationPatchPreBookingProductData =
  Booking.Components['PatchProductResponse'];

interface UseMutationPatchPreBookingProductError {
  errors: any;
  message?: string;
}

interface UseMutationPatchPreBookingProductParameters {
  brand?: Content.Properties.Brand;
  data: Booking.Components['PatchProductBodyRequest'];
  invalidationKeys?: QueryId[];
  parameters: Content.Components['IBookingHeadlessParameters'] & {
    id: string;
    pageId: string;
  };
}

const useMutationPatchPreBookingProduct = () => {
  const locale = getLocale();
  const queryClient = useQueryClient();

  const mutation: UseMutationResult<
    UseMutationPatchPreBookingProductData,
    UseMutationPatchPreBookingProductError,
    UseMutationPatchPreBookingProductParameters
  > = useMutation(
    async parameters => {
      const fetchResponse = await fetch(
        '/api/booking/pre-booking/products/patch-product',
        {
          body: JSON.stringify({
            bookingStateId: parameters.parameters.bookingStateId,
            brand: parameters.brand,
            data: parameters.data,
          }),
          headers: {
            ['Accept-Language']: getCulture(locale),
            ['Content-Type']: 'application/json',
          },
          method: 'PATCH',
        },
      )
        .then(response => {
          if (response.ok) return response.json();

          throw new Error(`${response.status} - ${response.statusText}`);
        })
        .catch(error => {
          console.error('bookingFlow.useMutationPatchPreBookingProduct', error);

          throw error;
        });

      return fetchResponse;
    },
    {
      onError: async error => error,
      onSuccess: (_data, parameters) => {
        parameters.invalidationKeys?.forEach(queryId => {
          queryClient.invalidateQueries([queryId]);
        });

        MY_TRIP_PRODUCT_INVALIDATION_KEYS.forEach(queryId => {
          queryClient.invalidateQueries([queryId]);
        });

        TOTAL_PRICE_DEPENDENT_QUERIES.forEach(queryId => {
          queryClient.invalidateQueries([queryId]);
        });
      },
    },
  );

  return mutation;
};

export default useMutationPatchPreBookingProduct;
